import React, { useEffect, useState } from "react";
import SidebarFilter from "../components/catalog/SidebarFilter";
import ProductCard from "../components/catalog/ProductCard";
import { supabase } from "../lib/supabase";

interface Product {
  id: string;
  title?: string;
  name?: string;
  price?: string;
  imageUrl?: string;
  Images?: string[];
  category?: string;
  Category?: string;
  brand?: string;
  Brand?: string;
  oldPrice?: string;
}

export const categories = [
  { name: "Mobilya ve Gölgelik", image: "/images/aksesuarlar/webps/mobilya.webp", items: ["Koltuk ve Masa", "Tente ve Güneşlik", "Krom Vardevela"] },
  { name: "Güverte", image: "/images/aksesuarlar/webps/guverte.webp", items: ["Bayrak ve Bayrak Direği", "Dolum Ağzı", "Döşemelik", "Fleto Bandı", "Halat", "Kakıç", "Kamış Yuvası", "Kampana", "Kanca", "Kaymaz Malzeme", "Kilit", "Klips", "Koç Boynuzu ve Kurtağzı", "Kulp ve Tutamak", "Küpeşte Fitili", "Lift ve Amortisör", "Mapa", "Matafora", "Menteşe", "Merdiven", "Pasarella", "Paslanmaz Malzeme", "Plastik Kapak, Dolap", "Rüzgar Yön Göstergesi", "Tahliye Borusu, Firar, Miçoz", "Tente Döşeme Malzemesi", "Krom vardevela"] },
  { name: "Navigasyon", image: "/images/aksesuarlar/webps/navigasyon.webp", items: ["Pusula ve Balık Bulucu Modelleri"] },
  { name: "Havalandırma", image: "/images/aksesuarlar/webps/havalandirma.webp", items: ["Tüm Havalandırma Ürünleri"] },
  { name: "Güvenlik", image: "/images/aksesuarlar/webps/guvenlik.webp", items: ["Gaz Alarmı", "Can Salı"] },
  { name: "Mutfak Malzemeleri", image: "/images/aksesuarlar/webps/mutfak.webp", items: ["Ocak", "Evye - Lavabo"] },
  { name: "Römork", image: "/images/aksesuarlar/webps/romork.webp", items: ["Römork", "Römork Ekipmanları"] },
  { name: "Sintine Pompası", image: "/images/aksesuarlar/webps/sintine.webp", items: ["Otomatik", "Manuel"] },
  { name: "Tatlı Su", image: "/images/aksesuarlar/webps/tatlisu.webp", items: ["Duş ve Duş Yuvası", "Esnek Su Tankları", "Gösterge", "Hidrofor", "Musluk", "Plastik Su Tankları", "Pompa", "Solar Duş", "Şamandıra"] },
  { name: "Tuvalet & Pis Su", image: "/images/aksesuarlar/webps/pis-su.webp", items: ["Duş Boşaltma Sistemi", "Esnek Pis Su Tankı", "Gösterge", "Güverte Çıkış Ağzı", "Koku Filtresi", "Pis Su Hortumu", "Pis Su Pompası", "Plastik Pis Su Tankı", "Şamandıra", "Tuvalet"] },
  { name: "Usturmaça", image: "/images/aksesuarlar/webps/usturmaca.webp", items: ["Usturmaça ve Ekipmanı"] },
  { name: "Yakıt Sistemi", image: "/images/aksesuarlar/webps/yakit.webp", items: ["Benzin Konnektörü", "Esnek Yakıt Tankı", "Koku Filtresi", "Plastik Yakıt Tankı", "Portatif Yakıt Tankı", "Tank Havalandırma", "Yakıt Filtresi", "Yakıt Göstergesi", "Yakıt Hortumu", "Yakıt Şamandırası", "Yakıt Taşıma Bidonu"] },
  { name: "Isıtıcı Sistemleri", image: "/images/aksesuarlar/webps/webasto.webp", items: ["Webasto Isıtıcı"] },
  { name: "Aydınlatma", image: "/images/aksesuarlar/webps/aydinlatma.webp", items: ["İskele Sancak Işıkları", "Seyir Feneri", "Projektör", "İç Aydınlatma", "Su Altı Aydınlatma"] },
];

const categoryNames = categories.map((c) => c.name);

const getTitle = (p: Product) => p.title || p.name || "";
const getCategory = (p: Product) => p.Category || p.category || "";
const getImage = (p: Product) => {
  if (p.Images && p.Images.length > 0) return p.Images[0];
  return p.imageUrl || "";
};

const AccessoriesPage: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    const fetchProducts = async () => {
      const { data, error } = await supabase.from("products").select("*");
      if (!error && data) setProducts(data as Product[]);
      setLoading(false);
    };
    fetchProducts();
  }, []);

  const filtered = products.filter((p) => {
    const matchesSearch = getTitle(p).toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory ? getCategory(p) === selectedCategory : true;
    return matchesSearch && matchesCategory;
  });

  const categoryFilters = categoryNames.map((name) => ({
    name,
    count: products.filter((p) => getCategory(p) === name).length,
    active: selectedCategory === name,
    onClick: () => setSelectedCategory(selectedCategory === name ? null : name),
  }));

  return (
    <div className="w-full bg-[#f8f9fa] text-gray-800 font-montserrat min-h-screen">
      <div className="bg-white border-b py-4 px-6 md:px-12 mb-8">
        <div className="max-w-7xl mx-auto flex items-center text-sm text-gray-500">
          <span>Anasayfa</span>
          <span className="mx-2">/</span>
          <span className="text-gray-900 font-semibold">Aksesuarlar</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 pb-12">
        <div className="flex flex-col md:flex-row gap-8">
          <div className="w-full md:w-64 flex-shrink-0">
            <SidebarFilter
              title="Aksesuarlar"
              categories={[
                {
                  name: "Tüm Ürünler",
                  active: !selectedCategory,
                  onClick: () => setSelectedCategory(null),
                  count: products.length,
                },
                ...categoryFilters,
              ]}
              onSearch={setSearchQuery}
            />
          </div>

          <div className="flex-1">
            <div className="bg-white p-4 rounded border mb-6 flex justify-between items-center text-sm">
              <div>
                Toplam <span className="font-bold">{filtered.length}</span> ürün
              </div>
            </div>

            {loading ? (
              <div className="text-center py-20">Yükleniyor...</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-20 bg-white rounded border">
                Bu kategoride ürün bulunamadı.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filtered.map((p) => (
                  <ProductCard
                    key={p.id}
                    id={p.id}
                    title={getTitle(p)}
                    price={p.price || ""}
                    image={getImage(p)}
                    brand={p.brand || p.Brand || ""}
                    linkPrefix="/urunler/aksesuar"
                    oldPrice={p.oldPrice || undefined}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccessoriesPage;
